import { CheckIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import React, { Dispatch, SetStateAction } from 'react';

import { Command, CommandGroup, CommandItem, CommandList, CommandSeparator } from 'components/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/shadcn/popover';
import { useLanguage } from 'languages/languageContext';
import { getConfig } from 'config/config';
import { makeStyles } from '@material-ui/styles';

const { theme } = getConfig();
interface SortingBarProps {
  disabled?: boolean;
  value: SortingOption;
  onChange: (option: SortingOption) => void;
}

export type SortingDirection = 'asc' | 'desc';
export interface SortingOption {
  label: string;
  value: 'name' | 'updatedAt' | 'createdAt';
  direction: SortingDirection;
}

const options: SortingOption[] = [
  {
    label: 'name',
    value: 'name',
    direction: 'asc',
  },
  // {
  //   label: 'dateModified',
  //   value: 'updatedAt',
  //   direction: 'asc',
  // },
  {
    label: 'dateCreated',
    value: 'createdAt',
    direction: 'asc',
  },
];

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 10,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  iconWrapper: {
    borderRadius: '50%',
    width: 25,
    height: 25,
    backgroundColor: theme.BUTTON_PRIMARY,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  popover: {
    padding: 0,
  },
  commandList: {
    padding: 0,
  },
  commandItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  commandItemLabel: {
    flex: 1,
    fontSize: 14,
  },
  commandItemCheckWrapper: {
    width: 16,
    height: 16,
  },
});
export const DocumentsSortingBar: React.FC<SortingBarProps> = ({ value, onChange }) => {
  const { documentsSortingBarComponent } = useLanguage();
  const styles = useStyles({});
  const optionRef = React.useRef<SortingOption>(value);

  const handleOptionSelect = React.useCallback(
    (option: SortingOption) => {
      onChange(option);
      optionRef.current = option;
    },
    [onChange],
  );

  const handleDirectionSelect = React.useCallback(
    (direction: SortingDirection) => {
      const option = { ...optionRef.current, direction };
      onChange(option);
      optionRef.current = option;
    },
    [onChange],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className={styles.wrapper}>
          <button className={styles.button}>
            <span>{documentsSortingBarComponent.sortBy + ': ' + documentsSortingBarComponent[value.label]}</span>
            <div className={styles.iconWrapper}>
              <ChevronUpIcon
                style={{
                  width: 21,
                  height: 21,
                  transform: value.direction === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
                }}
              />
            </div>
          </button>
        </div>
      </PopoverTrigger>
      <PopoverContent align={'start'} className={styles.popover}>
        <Command>
          <CommandList className={styles.commandList}>
            <ScrollArea style={{ height: 300, padding: 10 }}>
              <div>{documentsSortingBarComponent.sortBy}</div>
              <CommandGroup>
                {options
                  .filter((x) => x.value)
                  .map((option) => (
                    <CommandItem
                      className={styles.commandItem}
                      key={option.value}
                      onSelect={() => handleOptionSelect(option)}
                    >
                      <div className={styles.commandItemCheckWrapper}>
                        {optionRef.current.label === option.label && <CheckIcon />}
                      </div>

                      <span className={styles.commandItemLabel}>{documentsSortingBarComponent[option.label]}</span>
                    </CommandItem>
                  ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem onSelect={() => handleDirectionSelect('asc')}>
                  <div className={styles.commandItemCheckWrapper}>
                    {optionRef.current.direction === 'asc' && <CheckIcon />}
                  </div>
                  <span className={styles.commandItemLabel}>
                    {documentsSortingBarComponent.order[optionRef.current.label]['asc']}
                  </span>
                </CommandItem>
                <CommandItem onSelect={() => handleDirectionSelect('desc')}>
                  <div className={styles.commandItemCheckWrapper}>
                    {optionRef.current.direction === 'desc' && <CheckIcon />}
                  </div>
                  <span className={styles.commandItemLabel}>
                    {documentsSortingBarComponent.order[optionRef.current.label]['desc']}
                  </span>
                </CommandItem>
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
